// ---------------------------------------------------------------------------
// The single source of rendered clock time. Every timer on every screen reads
// through here.
//
// Two rules are load-bearing for fairness, not polish:
//
//   1. Remaining is recomputed from the Clock against serverNow(), on every
//      render, by both hooks below. Nothing counts down locally: a local counter
//      drifts, and it fast-forwards across a server restart. The device clock
//      may be minutes out, which is what serverNow() corrects for.
//   2. The rendered clock freezes ONLY on a server pause or on a conundrum buzz
//      held by somebody else, both of which arrive as `opts.frozen`. A player's
//      own disconnect freezes nobody's clock, including their own, because the
//      round is still running: a stopped clock would show them time they do not
//      have, and they would rightly believe they had been cheated out of it.
//   3. A frame is not the only thing that repaints it, and that is rule 2's
//      other half. `requestAnimationFrame` is the right clock for a disc that
//      has to move smoothly, and it is not a clock at all on a phone: WebKit
//      throttles frames hard while the visual viewport is being scrolled, and
//      with a keyboard up the visual viewport is in a scrolled state for as long
//      as the keyboard is there. The letters round came back from a phone with
//      "the readout is frozen in keyboard mode", which is a stopped clock in
//      front of a player who is still being charged for the time.
//
//      So a slow interval runs beside the frame loop. It is not a fallback that
//      has to detect anything — both simply request a repaint, every render
//      recomputes from the clock, and whichever of them the browser is willing
//      to run is enough to keep the seconds honest. It is torn down by exactly
//      the same `running` guard, so a frozen or expired clock still costs
//      nothing.
//
// ## Why there are two hooks
//
// There used to be one, and it ran the frame loop for every caller — so the
// whole of a play screen re-rendered sixty times a second for the length of
// every round, to move one wedge. The board, the answer box, the words-tried
// list and the players strip were all re-run per frame and all produced exactly
// what they produced the frame before.
//
// Only the DISC needs frame resolution. Everything else a reading is used for
// is whole seconds (`seconds`, and the aria label built from it), a threshold
// that moves once a round (`urgent`, at 20%), or a boolean the server decides
// anyway (`expired`, `frozen`). Four times a second is more than enough for all
// of them, and it is what `FALLBACK_MS` was already delivering on a phone that
// would not hand out frames.
//
// So `useClock` is the interval, for logic and for text, and `useSmoothClock`
// adds the frame loop on top for the one component that draws the wedge. The
// arithmetic is identical and both recompute from `serverNow()` on every render
// — rule 1 is untouched, and a smooth reading and a slow one taken in the same
// frame agree exactly. What changed is how often a repaint is asked for, never
// what a repaint computes.
//
// **Do not "simplify" this back to one hook.** The screens must not subscribe
// to frames, and the disc must. That is the whole point.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { fractionRemaining, remainingMs } from '@countdown/shared';
import type { Clock } from '@countdown/shared';
import { serverNow } from '../store/gameStore.js';

export interface ClockReading {
  /** 0 to 1, for the pie. */
  remaining: number;
  /** Whole seconds left, for aria labels and microcopy. Never rendered inside the disc. */
  seconds: number;
  /** True below 20%, so the caller can pass variant='alert'. */
  urgent: boolean;
  /** True when the clock is paused, whether by the server or by a buzz. */
  frozen: boolean;
  expired: boolean;
}

const URGENT_FRACTION = 0.2;

/**
 * How often the clock is repainted when frames are not being handed out — and,
 * since the split above, how often it is repainted for everybody who is not
 * drawing the disc.
 *
 * Four times a second, which is enough that a whole-second readout can never sit
 * on a number for longer than it is true, and slow enough to be free. It is not
 * the animation: `useSmoothClock` asks for frames as well, and when they are
 * flowing they arrive fifteen times as often.
 */
const FALLBACK_MS = 250;

const IDLE: ClockReading = {
  remaining: 0,
  seconds: 0,
  urgent: false,
  frozen: false,
  expired: false,
};

function readClock(clock: Clock | null, externallyFrozen: boolean): ClockReading {
  if (clock === null) return externallyFrozen ? { ...IDLE, frozen: true } : IDLE;

  const now = serverNow();
  const leftMs = remainingMs(clock, now);
  const remaining = fractionRemaining(clock, now);
  const expired = leftMs <= 0;

  return {
    remaining,
    seconds: Math.ceil(leftMs / 1000),
    urgent: !expired && remaining < URGENT_FRACTION,
    // A clock the server has already stopped carries its own pausedAt, so both
    // routes to a stopped clock report the same way to the caller.
    frozen: externallyFrozen || clock.pausedAt !== null,
    expired,
  };
}

/**
 * The shared half: derive the reading, and say whether anything still moves.
 *
 * The reading is derived during render rather than mirrored into state. Time is
 * not something these hooks own, so there is nothing to keep in sync: the loops
 * below exist only to force the re-render, and every render recomputes from the
 * clock. Mirroring it would put a phase change one frame behind, which is the
 * wrong frame to be behind on.
 */
function useReading(clock: Clock | null, externallyFrozen: boolean) {
  const reading = readClock(clock, externallyFrozen);
  const [, requestRepaint] = useState(0);

  // A frozen or expired clock has nothing left to animate, and a leaked loop per
  // screen transition costs a phone its battery.
  const running = clock !== null && !reading.frozen && !reading.expired;

  return { reading, running, requestRepaint };
}

/**
 * The reading, refreshed four times a second.
 *
 * What every screen wants. Whole seconds, the urgent threshold, and whether the
 * clock has stopped — none of which can change usefully faster than this, and
 * all of which used to cost a re-render of the entire screen per frame.
 */
export function useClock(clock: Clock | null, opts?: { frozen?: boolean }): ClockReading {
  const externallyFrozen = opts?.frozen ?? false;
  const { reading, running, requestRepaint } = useReading(clock, externallyFrozen);

  useEffect(() => {
    if (!running) return;

    // `running` is the only thing this reads, and the timer body closes over
    // nothing else. It deliberately does NOT depend on `clock`: a Clock arrives
    // as a fresh object on every broadcast, so depending on it tore the timer
    // down and built it again about once a second, for a callback that would
    // have behaved identically. Nothing about the new object needs a new timer —
    // the next render reads it, because the reading is derived rather than
    // stored.
    const timer = window.setInterval(() => {
      requestRepaint((count) => count + 1);
    }, FALLBACK_MS);

    return () => window.clearInterval(timer);
  }, [running, requestRepaint]);

  return reading;
}

/**
 * The reading, refreshed every frame the browser will give, and four times a
 * second regardless.
 *
 * For the disc and nothing else. A caller of this re-renders at frame rate for
 * the length of the round, so it must be a component that draws the wedge and
 * holds nothing else — see `components/primitives/LiveClock.tsx`, which is the
 * only caller and exists so that this cost lands on one small subtree instead of
 * on a whole screen.
 */
export function useSmoothClock(clock: Clock | null, opts?: { frozen?: boolean }): ClockReading {
  const externallyFrozen = opts?.frozen ?? false;
  const { reading, running, requestRepaint } = useReading(clock, externallyFrozen);

  useEffect(() => {
    if (!running) return;

    let handle = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      // The re-render recomputes `running`; when the clock expires this effect
      // tears itself down on the next commit.
      requestRepaint((count) => count + 1);
      handle = requestAnimationFrame(tick);
    };

    handle = requestAnimationFrame(tick);

    // The same request, on a clock the browser cannot throttle away. See rule 3.
    const timer = window.setInterval(() => {
      if (cancelled) return;
      requestRepaint((count) => count + 1);
    }, FALLBACK_MS);

    return () => {
      cancelled = true;
      cancelAnimationFrame(handle);
      window.clearInterval(timer);
    };
  }, [running, requestRepaint]);

  return reading;
}
